import { useEffect, useMemo, useState } from "react";
import { IRole } from "../models/IUserRoles";
import { createRole, getRoleList } from "../services/auth";

const MAX_NAME = 100;
const MAX_DESCRIPTION = 500;

/** Yanıt eski sarmalayıcıyla ({ roleList }) gelirse de okunabilsin. */
function toRoleArray(data: unknown): IRole[] {
  if (Array.isArray(data)) return data as IRole[];
  const wrapped = (data as { roleList?: IRole[] } | null)?.roleList;
  return Array.isArray(wrapped) ? wrapped : [];
}

function errorText(err: any, fallback: string): string {
  const status = err?.response?.status;
  if (status === 403) return "Bu işlem için yetkiniz yok.";
  const data = err?.response?.data;
  if (Array.isArray(data) && data[0]?.description) return data[0].description;
  const firstValidation = data?.errors && Object.values(data.errors as Record<string, string[]>)[0]?.[0];
  return firstValidation ?? fallback;
}

export default function RolesTab() {
  const [roles, setRoles] = useState<IRole[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    getRoleList()
      .then((res) => setRoles(toRoleArray(res.data)))
      .catch((err) => {
        // Hiç rol yoksa uç RoleNotExist hatası döner; bu boş liste demektir.
        const code = Array.isArray(err?.response?.data) ? err.response.data[0]?.code : undefined;
        if (code === "RoleNotExist") { setRoles([]); return; }
        setRoles(null);
        setError(errorText(err, "Roller alınamadı."));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const list = [...(roles ?? [])].sort((a, b) => (a.roleName ?? "").localeCompare(b.roleName ?? "", "tr"));
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((r) =>
      (r.roleName ?? "").toLocaleLowerCase("tr").includes(q) ||
      (r.roleDescription ?? "").toLocaleLowerCase("tr").includes(q) ||
      r.id.toLowerCase().includes(q)
    );
  }, [roles, search]);

  const copyId = (id: string) => {
    navigator.clipboard?.writeText(id)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
      })
      .catch(() => {});
  };

  const trimmedName = name.trim();
  // Backend aynı isimde rol var mı denetlemiyor; çift kayıt burada engellenir.
  const duplicate = !!trimmedName && (roles ?? []).some(
    (r) => (r.roleName ?? "").trim().toLocaleLowerCase("tr") === trimmedName.toLocaleLowerCase("tr")
  );

  const resetForm = () => {
    setName("");
    setDescription("");
    setIsEnabled(true);
    setFormError("");
  };

  const handleCreate = () => {
    if (!trimmedName) { setFormError("Rol adı zorunludur."); return; }
    if (duplicate) { setFormError("Bu isimde bir rol zaten var."); return; }
    setSaving(true);
    setFormError("");
    createRole({
      roleName: trimmedName,
      roleDescription: description.trim() || undefined,
      roleIsEnabled: isEnabled,
    })
      .then((res) => {
        const created = res.data;
        if (created?.id) setHighlightId(created.id);
        resetForm();
        setShowCreate(false);
        setSearch("");
        load();
      })
      .catch((err) => setFormError(errorText(err, "Rol oluşturulamadı.")))
      .finally(() => setSaving(false));
  };

  const activeCount = (roles ?? []).filter((r) => r.roleIsEnabled).length;

  return (
    <div className="roles">
      <div className="roles__toolbar">
        <div className="roles__search">
          <i className="bi bi-search"></i>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rol adı, açıklama ya da id ile ara..." />
          {search && <button type="button" onClick={() => setSearch("")}><i className="bi bi-x-lg"></i></button>}
        </div>
        <button type="button" className="roles__btn roles__btn--ghost" onClick={load} disabled={loading}>
          <i className={`bi bi-arrow-clockwise${loading ? " roles__spin" : ""}`}></i>
          <span>Yenile</span>
        </button>
        <button type="button" className="roles__btn roles__btn--primary"
          onClick={() => { resetForm(); setShowCreate(true); }}>
          <i className="bi bi-plus-lg"></i>
          <span>Yeni Rol</span>
        </button>
      </div>

      {error && (
        <div className="roles__alert roles__alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      )}

      {loading && !roles ? (
        <div className="roles__center"><span className="spinner-border" /></div>
      ) : roles && (
        <>
          <div className="roles__summary">
            <span><strong>{roles.length}</strong> rol</span>
            <span className="roles__dot">·</span>
            <span><strong className="roles__ok">{activeCount}</strong> aktif</span>
          </div>

          {visible.length === 0 ? (
            <div className="roles__center">
              <i className="bi bi-shield-x"></i>
              <div>{search ? `"${search}" ile eşleşen rol yok.` : "Tanımlı rol yok."}</div>
            </div>
          ) : (
            <div className="roles__grid">
              {visible.map((r) => (
                <div key={r.id}
                  className={`role-card${r.roleIsEnabled ? "" : " role-card--off"}${highlightId === r.id ? " role-card--new" : ""}`}>
                  <div className="role-card__top">
                    <div className="role-card__icon"><i className="bi bi-shield-lock-fill"></i></div>
                    <div className="role-card__title">
                      <div className="role-card__name">{r.roleName}</div>
                      {highlightId === r.id && <span className="role-card__new-badge">Yeni</span>}
                    </div>
                    <span className={`role-card__status${r.roleIsEnabled ? " role-card__status--on" : ""}`}>
                      {r.roleIsEnabled ? "Aktif" : "Pasif"}
                    </span>
                  </div>

                  <div className="role-card__desc">
                    {r.roleDescription?.trim() || <span className="role-card__muted">Açıklama yok</span>}
                  </div>

                  <div className="role-card__id">
                    <span className="role-card__id-label">Rol Id</span>
                    <code title={r.id}>{r.id}</code>
                    <button type="button" className="role-card__copy" onClick={() => copyId(r.id)}
                      title="Id'yi kopyala" aria-label="Rol id'sini kopyala">
                      {copiedId === r.id
                        ? <><i className="bi bi-check2"></i><span>Kopyalandı</span></>
                        : <i className="bi bi-clipboard"></i>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <div className="roles__backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) setShowCreate(false); }}>
          <div className="roles__modal" role="dialog" aria-modal="true">
            <div className="roles__modal-head">
              <div>
                <div className="roles__modal-title"><i className="bi bi-shield-plus me-2"></i>Yeni Rol</div>
                <div className="roles__modal-sub">Oluşturulan rolün id'si listede görünür.</div>
              </div>
              <button type="button" className="roles__icon-btn" onClick={() => setShowCreate(false)} disabled={saving}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="roles__modal-body">
              <label className="roles__label">Rol Adı <span className="roles__req">*</span></label>
              <input className={`form-control${duplicate ? " is-invalid" : ""}`} value={name} maxLength={MAX_NAME}
                autoFocus disabled={saving} placeholder="ör. RestaurantCashier"
                onChange={(e) => { setName(e.target.value); setFormError(""); }} />
              <div className="roles__hint">
                {duplicate
                  ? <span className="roles__err">Bu isimde bir rol zaten var.</span>
                  : <>Rol adı yetki kontrollerinde birebir kullanılır; boşluksuz ve İngilizce yazılması önerilir.</>}
              </div>

              <label className="roles__label" style={{ marginTop: 12 }}>Açıklama</label>
              <textarea className="form-control" rows={3} value={description} maxLength={MAX_DESCRIPTION}
                disabled={saving} placeholder="Bu rol ne için kullanılacak?"
                onChange={(e) => setDescription(e.target.value)} />
              <div className="roles__hint roles__hint--right">{description.length} / {MAX_DESCRIPTION}</div>

              <label className="roles__switch">
                <input type="checkbox" checked={isEnabled} disabled={saving}
                  onChange={(e) => setIsEnabled(e.target.checked)} />
                <span>Aktif olarak oluştur</span>
              </label>

              <div className="roles__alert roles__alert--info">
                <i className="bi bi-info-circle-fill"></i>
                <span>
                  Yeni rol tek başına bir yetki açmaz; backend'de bu role izin veren uçlar tanımlanana
                  kadar yalnızca kullanıcılara atanabilen bir etiket olarak durur.
                </span>
              </div>

              {formError && (
                <div className="roles__alert roles__alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{formError}</span></div>
              )}
            </div>

            <div className="roles__modal-foot">
              <button type="button" className="roles__btn roles__btn--ghost" onClick={() => setShowCreate(false)} disabled={saving}>
                Vazgeç
              </button>
              <button type="button" className="roles__btn roles__btn--primary" onClick={handleCreate}
                disabled={saving || !trimmedName || duplicate}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm" /><span>Oluşturuluyor...</span></>
                  : <><i className="bi bi-check-lg"></i><span>Rolü Oluştur</span></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
